import { Checkbox, Grid, Link, Typography } from '@mui/material';

import { useState, useEffect } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import ButtonComp from 'common/components/Button/ButtonComp';
import ModalEle from 'common/components/Modal/ModalEle';
import { HTTP_STATUS_CODES } from 'common/constants/HttpStatusConstants';
import {
	LoggedInUserTermsAndPolicyStatus,
	TermsAndPolicyDisplayText,
	TermsAndPolicyStatementLink,
} from 'common/constants/UserManagementConstants';
import localStorageWrapper from 'common/loginUtils/local-storage-wrapper';
import { RootState } from 'common/store/AppStore';
import {
	setLoggedInUser,
	setToastMessage,
} from 'common/store/reducer/commonReducer';
import { onLogout } from 'common/utils/authCheck';

import {
	getUserInfo,
	updateTermsAndPolicyStatus,
} from 'features/Settings/UserManagement/api';

import styles from './TermsAndPolicyModal.module.scss';

const TermsAndPolicyModal = () => {
	const { OK, BAD_REQUEST, INTERNAL_SERVER_ERROR } = HTTP_STATUS_CODES;

	const { checkboxCheckedcolor } = styles;
	const loggedInUser = useSelector(
		(state: RootState) => state.common.loggedInUser,
	);
	const [checked, setChecked] = useState(false);
	const [acceptButtonEnabled, setAcceptButtonEnabled] = useState(false);
	const [declineButtonEnabled, setDeclineButtonEnabled] = useState(false);
	const [modalVisible, setModalVisible] = useState(false);
	const [declineButtonClicked, setDeclineButtonClicked] = useState(false);
	const [, setAccepted] = useState(false);

	const { ACCEPTED, DECLINED } = LoggedInUserTermsAndPolicyStatus;
	const { TERMS_OF_USE, POLICY_STATEMENT } = TermsAndPolicyStatementLink;
	const { TERMS_POLICY_MODAL_TITLE, TERMS_OF_USE_TEXT, POLICY_STATEMENT_TEXT } =
		TermsAndPolicyDisplayText;

	const dispatch = useDispatch();

	useEffect(() => {
		const localStorageAccepted = localStorageWrapper.getStorageItem(
			`accepted_${loggedInUser?.userId}`,
		);
		if (localStorageAccepted) {
			setAccepted(true);
			setModalVisible(false);
		} else if (loggedInUser) {
			if (
				!Object.prototype.hasOwnProperty.call(
					loggedInUser,
					'termsAcknowledgeStatus',
				)
			) {
				setModalVisible(true);
				setAcceptButtonEnabled(checked);
				setDeclineButtonEnabled(checked);
			} else if (loggedInUser.termsAcknowledgeStatus === DECLINED) {
				setModalVisible(true);
				setAcceptButtonEnabled(checked);
				setDeclineButtonEnabled(false);
				setDeclineButtonClicked(true);
			} else if (loggedInUser.termsAcknowledgeStatus === ACCEPTED) {
				setModalVisible(false);
			}
		}
	}, [loggedInUser, checked]);

	const handleAcceptButtonClick = () => {
		const status = ACCEPTED;
		updateStatus(status);
		setModalVisible(false);
		localStorageWrapper.setStorageItem(
			`accepted_${loggedInUser?.userId}`,
			true,
		);
	};

	const handleDeclineButtonClick = async () => {
		if (!declineButtonClicked) {
			setDeclineButtonClicked(true);
			setDeclineButtonEnabled(false);
			setAcceptButtonEnabled(false);
			const status = DECLINED;
			await updateStatus(status);
			setAcceptButtonEnabled(false);
			onLogout();
		}
	};

	useEffect(() => {
		setDeclineButtonEnabled(checked && !declineButtonClicked);
	}, [checked, declineButtonClicked]);

	const handleCheckboxChange = () => {
		setChecked(!checked);
	};

	const updateStatus = async (status: string) => {
		const payload = {
			firstName: loggedInUser?.firstName,
			lastName: loggedInUser?.lastName,
			termsAcknowledgeStatus: status,
			userId: loggedInUser?.userId,
			groups: loggedInUser?.groups,
			roles: loggedInUser?.roles,
		};
		const response = await updateTermsAndPolicyStatus(
			loggedInUser?.userId as string,
			payload,
		);
		if (response.statusCode === OK && status === ACCEPTED) {
			setModalVisible(false);
		} else if (
			response.statusCode === BAD_REQUEST ||
			response.statusCode === INTERNAL_SERVER_ERROR
		) {
			if (response?.errorBody || response?.error) {
				const { errorBody } = response;
				const responseError: any = response.error;
				const errorMsg =
					(errorBody?.message as string) || (responseError?.message as string);
				dispatch(
					setToastMessage({
						message: errorMsg,
						severity: 'error',
						isToastOpen: true,
					}),
				);
			}
			setModalVisible(true);
		}
		const userInfo = await getUserInfo();
		if (userInfo?.statusCode === OK && userInfo.responseBody) {
			dispatch(setLoggedInUser(userInfo?.responseBody));
		}
	};

	return (
		<ModalEle
			open={modalVisible}
			data-testid="terms-and-policy-modal"
			sxBoxProps={{ width: 'auto', height: '20%', margin: 'auto' }}
		>
			<Grid container direction="column" spacing={2}>
				<Grid item>
					<Typography variant="h6">{TERMS_POLICY_MODAL_TITLE}</Typography>
				</Grid>
				<Grid item>
					<Grid container alignItems="center">
						<Grid item xs={1} sm={1} md={1} lg={1}>
							<Checkbox
								disableRipple
								disableTouchRipple
								checked={checked}
								onChange={handleCheckboxChange}
								sx={{
									'&.Mui-checked': {
										color: checkboxCheckedcolor,
									},
								}}
								data-testid="terms-and-policy-modal-checkbox'"
							/>
						</Grid>
						<Grid item xs={11} sm={11} md={11} lg={11}>
							<Typography variant="body1">
								I have read and agree to the{' '}
								<Link
									href={TERMS_OF_USE}
									target="_blank"
									rel="noopener"
									className={styles['terms-policy-link-color']}
								>
									{TERMS_OF_USE_TEXT}
								</Link>{' '}
								and{' '}
								<Link
									href={POLICY_STATEMENT}
									target="_blank"
									rel="noopener"
									className={styles['terms-policy-link-color']}
								>
									{POLICY_STATEMENT_TEXT}
								</Link>
							</Typography>
						</Grid>
					</Grid>
				</Grid>
				<Grid item>
					<Grid container justifyContent="flex-end" spacing={2}>
						<Grid item>
							<ButtonComp
								btnType="secondary"
								onClick={handleDeclineButtonClick}
								disabled={!declineButtonEnabled}
								data-testid="decline-dialog-btn"
							>
								Decline
							</ButtonComp>
						</Grid>
						<Grid item>
							<ButtonComp
								btnType="primary"
								onClick={handleAcceptButtonClick}
								disabled={!acceptButtonEnabled}
								data-testid="accept-dialog-btn"
							>
								Accept
							</ButtonComp>
						</Grid>
					</Grid>
				</Grid>
			</Grid>
		</ModalEle>
	);
};

export default TermsAndPolicyModal;
